import {
  Client,
  ClientResponse,
  Conversation,
  ConversationContext,
  ConversationEventType,
  ConversationActionParams,
  ConversationMessage,
  ConversationParticipant,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  MessagePartActionParams,
  MessagePartSubmitParams,
  SendConversationMessageParams,
  SendFeedbackParams,
  StartConversationParams,
  StartConversationResponse,
  SendConversationTypingParams,
} from '@stripe-internal/conversations-client';
import {ActionHandler} from './Action';
import {GetConversationMessagesHandler} from './GetConversationMessages';
import {GetConversationParticipantsHandler} from './GetConversationParticipants';
import {LeaveConversationHandler} from './LeaveConversation';
import {PollConversationHandler} from './PollConversation';
import {SendConversationMessageHandler} from './SendConversationMessage';
import {SendFeedbackHandler} from './SendFeedback';
import {GetConversationHandler} from './GetConversation';
import {StartConversationHandler} from './StartConversation';
import {SubmitMessagePartHandler} from './SubmitMessagePart';
import type {StartConversationCreateProcessFailureMetricTags} from './StartConversation';
import {ApiMethods, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {InProgressHandler} from './InProgress';
import {SendConversationTypingHandler} from './SendConversationTyping';

/**
 * A collection of handler functions that implement the Client interface methods.
 *
 * Each handler transforms between the client-side conversation framework constructs
 * and API constructs - handling parameter validation, API request construction,
 * and response transformation via FrameworkAdapter.
 */
export type ClientHandlers = {
  getConversation: GetConversationHandler['call'];
  startConversation: (
    params: StartConversationParams,
  ) => Promise<StartConversationResponse>;
  leaveConversation: (
    params: LeaveConversationParams,
  ) => Promise<ClientResponse>;
  getConversationMessages: (
    params: GetConversationMessagesParams,
  ) => Promise<GetConversationMessagesResponse>;
  getConversationParticipants: (
    params?: GetConversationParticipantsParams,
  ) => Promise<GetConversationParticipantsResponse>;
  sendConversationMessage: (
    params: SendConversationMessageParams,
  ) => Promise<ClientResponse>;
  sendFeedback: (params: SendFeedbackParams) => Promise<ClientResponse>;
  action: (params: ConversationActionParams) => Promise<ClientResponse>;
  inProgress: (params: InProgressParams) => Promise<InProgressResponse>;
  sendConversationTyping: (
    params?: SendConversationTypingParams,
  ) => Promise<ClientResponse>;
  pollConversation: (params: {
    conversationId: string;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
    dynamicMessageIds?: string[];
  }) => Promise<
    | {
        type: 'success';
        messages: Record<string, ConversationMessage>;
        context?: ConversationContext;
      }
    | {type: 'error'; error: string; code: number}
  >;
  actOnMessagePart: (
    params: MessagePartActionParams,
  ) => Promise<ClientResponse>;
  submitMessagePart: (
    params: MessagePartSubmitParams,
  ) => Promise<ClientResponse>;
};

/**
 * Builds a set of client handlers that implement conversation operations.
 *
 * This factory is the dependency boundary between client.ts and operation
 * handlers. The parent branch keeps the handlers as stubs while sibling
 * branches wire their implemented handlers to ApiMethods, event emitters, and
 * callbacks.
 *
 * @param request - The fetch function used by ApiMethods to make HTTP requests
 * @param reportWarning - Reports non-fatal API parsing and adapter warnings
 * @param emitConversationEvent - Emits conversation events produced by handlers
 * @param baseUrl - Optional Conversations API base URL override
 * @returns A ClientHandlers object with methods for all conversation operations
 */
export function buildClientHandlers({
  client,
  request,
  reportWarning,
  emitConversationEvent,
  emitDelegateFirstMessageToServerFailureMetric,
  getParticipant,
  getAssistantParticipant,
  getCachedMessage,
  baseUrl,
  enableUhPendingInput,
  getCreateConversationHeaders,
  enableConversationsThreads,
}: {
  client: Client;
  request: (url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  reportWarning?: ReportWarning;
  emitConversationEvent: (event: ConversationEventType) => void;
  emitDelegateFirstMessageToServerFailureMetric?: (
    tags: StartConversationCreateProcessFailureMetricTags,
  ) => void;
  getParticipant: () => ConversationParticipant | undefined;
  getAssistantParticipant: () => ConversationParticipant;
  getCachedMessage: (
    conversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
  baseUrl?: string;
  enableUhPendingInput?: boolean;
  getCreateConversationHeaders?: () => Record<string, string>;
  // Threads-only: routes leaveConversation through the all-in-one end
  // endpoint (ends live chat/RAC/the conversation itself) instead of plain
  // conversation deletion, and surfaces backend-declared channel transitions
  // from GET/poll responses as context for Threads to settle. Leave unset for
  // non-Threads consumers, whose client drives transitions itself.
  enableConversationsThreads?: boolean;
}): ClientHandlers {
  const apiMethods = new ApiMethods({
    fetcher: request,
    reportWarning,
    baseUrl,
    getCreateConversationHeaders,
  });
  const attachmentDownloadPath = client.urls?.attachmentDownloadPath;
  const leaveConversationHandler = new LeaveConversationHandler({
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    endConversation: enableConversationsThreads
      ? (conversationId) => apiMethods.endConversation(conversationId)
      : undefined,
    emitConversationEvent,
    reportWarning,
  });
  const sendConversationMessageHandler = new SendConversationMessageHandler({
    sendMessage: (conversationId, payload, options) =>
      options?.allowNoContent === true
        ? apiMethods.sendMessage(conversationId, payload, {
            allowNoContent: true,
          })
        : apiMethods.sendMessage(conversationId, payload),
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    emitConversationEvent,
    reportWarning,
    enableUhPendingInput,
    attachmentDownloadPath,
    getCachedMessage,
  });
  const getConversationHandler = new GetConversationHandler({
    getConversation: (id) => apiMethods.getConversation(id),
    attachmentDownloadPath,
    reportWarning,
  });
  const startConversationHandler = new StartConversationHandler({
    createConversation: (payload) => apiMethods.createConversation(payload),
    sendMessage: (conversationId, payload) =>
      apiMethods.sendMessage(conversationId, payload),
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
    listConversations: (params) => apiMethods.listConversations(params),
    emitDelegateFirstMessageToServerFailureMetric,
    buildConversationInstance: ({frameworkConversation, params}) => {
      return new Conversation(client, {
        id: frameworkConversation.id,
        user: params.participant,
        status: frameworkConversation.status,
        syncStorage: params.syncStorage,
        messages: frameworkConversation.messages,
        participants: frameworkConversation.participants,
        channel: frameworkConversation.context.channel,
        context: frameworkConversation.context,
      });
    },
    enableUhPendingInput,
    attachmentDownloadPath,
    reportWarning,
  });
  const getConversationMessagesHandler = new GetConversationMessagesHandler({
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
    getParticipant,
    reportWarning,
    enableUhPendingInput,
    attachmentDownloadPath,
    includeTransitionContext: enableConversationsThreads,
  });
  const getConversationParticipantsHandler =
    new GetConversationParticipantsHandler({
      getParticipant,
      getAssistantParticipant,
    });
  const sendFeedbackHandler = new SendFeedbackHandler({
    sendMessage: (conversationId, payload, options) =>
      apiMethods.sendMessage(conversationId, payload, options),
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    emitConversationEvent,
    getParticipant,
    attachmentDownloadPath,
    reportWarning,
  });
  const submitMessagePartHandler = new SubmitMessagePartHandler({
    sendMessage: (conversationId, payload, options) =>
      options?.allowNoContent === true
        ? apiMethods.sendMessage(conversationId, payload, {
            allowNoContent: true,
          })
        : apiMethods.sendMessage(conversationId, payload),
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    emitConversationEvent,
    getParticipant,
    reportWarning,
    enableUhPendingInput,
    attachmentDownloadPath,
    getCachedMessage,
  });
  const actionHandler = new ActionHandler({
    sendMessage: (conversationId, payload) =>
      apiMethods.sendMessage(conversationId, payload),
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    emitConversationEvent,
    reportWarning,
    getParticipant,
    enableUhPendingInput,
    attachmentDownloadPath,
    getCachedMessage,
  });
  const inProgressHandler = new InProgressHandler({
    listConversations: (params) => apiMethods.listConversations(params),
  });
  const sendConversationTypingHandler = new SendConversationTypingHandler();
  const pollConversationHandler = new PollConversationHandler({
    pollConversation: (conversationId, messageIds) =>
      apiMethods.pollConversation(conversationId, messageIds),
    attachmentDownloadPath,
    reportWarning,
    includeTransitionContext: enableConversationsThreads,
  });

  return {
    getConversation: (params) => getConversationHandler.call(params),
    startConversation: (params) => startConversationHandler.call(params),
    leaveConversation: (params) => leaveConversationHandler.call(params),
    getConversationMessages: (params) =>
      getConversationMessagesHandler.call(params),
    getConversationParticipants: (params) =>
      getConversationParticipantsHandler.call(params),
    sendConversationMessage: (params) =>
      sendConversationMessageHandler.call(params),
    sendFeedback: (params) => sendFeedbackHandler.call(params),
    action: (params) => actionHandler.call(params),
    inProgress: (params) => inProgressHandler.call(params),
    sendConversationTyping: (params) =>
      sendConversationTypingHandler.call(params),
    pollConversation: (params) => pollConversationHandler.call(params),
    actOnMessagePart: async ({conversationId, messageId, partId, data}) => {
      const response = await apiMethods.actOnMessagePart(
        conversationId,
        messageId,
        partId,
        {data},
      );
      if (response.type === 'error') {
        return FrameworkAdapter.toClientErrorResponse(response);
      }

      return {code: 200, data: response.data};
    },
    submitMessagePart: (params) => submitMessagePartHandler.call(params),
  };
}
